import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  IMobileState,
  IMobileDashboardLayout,
  IMobileWidgetConfig,
  IPWASettings,
  IPWANotification,
  ITouchEvent,
  DeviceType,
  Orientation,
  IPerformanceMetrics,
} from '../types/mobile';

/** Расширения Navigator/Performance для нестандартных API (Battery, Network Information, Memory) */
interface NavigatorWithBattery extends Navigator {
  getBattery?: () => Promise<{ level: number }>;
  connection?: { effectiveType: string };
}
interface PerformanceWithMemory extends Performance {
  memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
}

// Начальное состояние
const initialState: IMobileState = {
  deviceType: 'desktop',
  orientation: 'landscape',
  viewport: {
    width: window.innerWidth,
    height: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
  },
  
  pwaSettings: {
    isInstalled: false,
    enableOffline: true,
    enablePushNotifications: false,
    enableBackgroundSync: true,
    cacheStrategy: 'network-first',
    updateStrategy: 'on-reload',
    offlinePages: ['/', '/dashboard', '/events', '/tasks'],
    notificationSettings: {
      enableSound: true,
      enableVibration: true,
      badge: true,
      silent: false,
      actions: [],
    },
  },
  
  isOnline: navigator.onLine,
  updateAvailable: false,
  
  mobileLayouts: [],
  currentLayout: null,
  
  touchEnabled: 'ontouchstart' in window,
  gesturesEnabled: true,
  lastTouch: null,
  
  ui: {
    showMobileMenu: false,
    showBottomNav: true,
    showFloatingActions: true,
    fullscreenWidget: null,
    swipeInProgress: false,
    pinchInProgress: false,
  },
  
  performance: {
    enableLazyLoading: true,
    enableVirtualization: true,
    reduceAnimations: false,
    lowDataMode: false,
  },
  
  loading: {
    layouts: false,
    installation: false,
  },
  
  error: null,
};

// Async thunks
export const detectDevice = createAsyncThunk(
  'mobile/detectDevice',
  async () => {
    const userAgent = navigator.userAgent;
    const width = window.innerWidth;
    
    let deviceType: DeviceType = 'desktop';
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      if (width < 768) {
        deviceType = 'mobile';
      } else {
        deviceType = 'tablet';
      }
    }
    
    const orientation: Orientation = width > window.innerHeight ? 'landscape' : 'portrait';
    
    return {
      deviceType,
      orientation,
      viewport: {
        width,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio || 1,
      },
      touchEnabled: 'ontouchstart' in window,
    };
  }
);

export const loadMobileLayouts = createAsyncThunk(
  'mobile/loadLayouts',
  async () => {
    // Mock данные - в будущем заменить на API
    const layouts: IMobileDashboardLayout[] = [
      {
        id: 'mobile-default',
        name: 'Мобильный дашборд',
        deviceType: 'mobile',
        orientation: 'portrait',
        isDefault: true,
        widgets: [
          {
            id: 'mobile-widget-1',
            widgetId: 'kpi-events',
            mobileSize: { cols: 2, rows: 1 },
            priority: 1,
            showInMobile: true,
            touchOptimized: true,
            swipeActions: {
              left: 'refresh',
              right: 'settings',
            },
            gestures: {
              doubleTap: 'expand',
              longPress: 'menu',
            },
          },
          {
            id: 'mobile-widget-2',
            widgetId: 'kpi-tasks',
            mobileSize: { cols: 2, rows: 1 },
            priority: 2,
            showInMobile: true,
            touchOptimized: true,
            swipeActions: {
              left: 'refresh',
              right: 'settings',
            },
            gestures: {
              doubleTap: 'expand',
              longPress: 'menu',
            },
          },
          {
            id: 'mobile-widget-3',
            widgetId: 'chart-events-trend',
            mobileSize: { cols: 2, rows: 2 },
            priority: 3,
            showInMobile: true,
            touchOptimized: true,
            swipeActions: {
              up: 'fullscreen',
            },
            gestures: {
              pinch: 'zoom',
              doubleTap: 'expand',
            },
          },
        ],
        settings: {
          gridCols: 2,
          rowHeight: 120,
          margin: 8,
          enableSwipe: true,
          enablePinchZoom: true,
          showScrollIndicator: true,
          infiniteScroll: false,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];
    
    return layouts;
  }
);

export const installPWA = createAsyncThunk(
  'mobile/installPWA',
  async () => {
    // Логика установки PWA
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.register('/sw.js');
      if (process.env['NODE_ENV'] === 'development') {
        // eslint-disable-next-line no-console
        console.log('SW registered: ', registration);
      }
    }
    
    return { isInstalled: true };
  }
);

export const sendPushNotification = createAsyncThunk(
  'mobile/sendPushNotification',
  async (notification: Omit<IPWANotification, 'id' | 'timestamp'>) => {
    if (!('Notification' in window)) {
      throw new Error('Браузер не поддерживает push-уведомления');
    }
    
    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Разрешение на уведомления не предоставлено');
      }
    }
    
    const fullNotification: IPWANotification = {
      ...notification,
      id: `notif_${Date.now()}`,
      timestamp: new Date(),
    };

    const options: NotificationOptions = {
      body: notification.body,
      data: notification.data,
    };

    if (notification.icon !== undefined) {
      options.icon = notification.icon;
    }
    if (notification.badge !== undefined) {
      options.badge = notification.badge;
    }
    if (notification.tag !== undefined) {
      options.tag = notification.tag;
    }
    if (notification.requireInteraction !== undefined) {
      options.requireInteraction = notification.requireInteraction;
    }
    if (notification.silent !== undefined) {
      options.silent = notification.silent;
    }

    new Notification(notification.title, options);
    
    return fullNotification;
  }
);

export const measurePerformance = createAsyncThunk(
  'mobile/measurePerformance',
  async (): Promise<IPerformanceMetrics> => {
    const performance = window.performance;
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    // Web Vitals
    const paintEntries = performance.getEntriesByType('paint');
    const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    
    let batteryLevel: number | undefined;
    let networkType: IPerformanceMetrics['networkType'];
    let memoryUsage: { used: number; total: number } = {
      used: 0,
      total: 0,
    };
    
    // Battery API (если поддерживается)
    if ('getBattery' in navigator) {
      try {
        const battery = await (navigator as NavigatorWithBattery).getBattery!();
        batteryLevel = battery.level;
      } catch (e) {
        // Игнорируем ошибки
      }
    }
    
    // Network Information API (если поддерживается)
    if ('connection' in navigator) {
      const connection = (navigator as NavigatorWithBattery).connection;
      const raw = connection?.effectiveType;
      const allowed: IPerformanceMetrics['networkType'][] = ['slow-2g', '2g', '3g', '4g', '5g', 'wifi'];
      networkType = raw && allowed.includes(raw as IPerformanceMetrics['networkType']) ? (raw as IPerformanceMetrics['networkType']) : undefined;
    }
    
    // Memory API (если поддерживается)
    if ('memory' in performance) {
      const memory = (performance as PerformanceWithMemory).memory;
      if (memory) {
        memoryUsage = {
          used: memory.usedJSHeapSize,
          total: memory.totalJSHeapSize,
        };
      }
    }
    
    return {
      firstContentfulPaint: fcpEntry ? fcpEntry.startTime : 0,
      largestContentfulPaint: 0, // Требует дополнительную библиотеку
      firstInputDelay: 0, // Требует дополнительную библиотеку
      cumulativeLayoutShift: 0, // Требует дополнительную библиотеку
      totalBlockingTime: navigation.loadEventEnd - navigation.loadEventStart,
      batteryLevel: batteryLevel ?? 0,
      // exactOptionalPropertyTypes: networkType должен быть всегда валидным значением union
      networkType: (networkType ?? 'wifi') as Exclude<
        IPerformanceMetrics['networkType'],
        undefined
      >,
      memoryUsage,
    };
  }
);

const mobileSlice = createSlice({
  name: 'mobile',
  initialState,
  reducers: {
    // Управление устройством и ориентацией
    setDeviceType: (state, action: PayloadAction<DeviceType>) => {
      state.deviceType = action.payload;
    },
    
    setOrientation: (state, action: PayloadAction<Orientation>) => {
      state.orientation = action.payload;
    },
    
    updateViewport: (state, action: PayloadAction<{ width: number; height: number }>) => {
      state.viewport.width = action.payload.width;
      state.viewport.height = action.payload.height;
    },
    
    // Управление PWA
    updatePWASettings: (state, action: PayloadAction<Partial<IPWASettings>>) => {
      state.pwaSettings = { ...state.pwaSettings, ...action.payload };
    },
    
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    
    setUpdateAvailable: (state, action: PayloadAction<boolean>) => {
      state.updateAvailable = action.payload;
    },
    
    // Touch события
    recordTouchEvent: (state, action: PayloadAction<ITouchEvent>) => {
      state.lastTouch = action.payload;
    },
    
    setGesturesEnabled: (state, action: PayloadAction<boolean>) => {
      state.gesturesEnabled = action.payload;
    },
    
    // UI управление
    setShowMobileMenu: (state, action: PayloadAction<boolean>) => {
      state.ui.showMobileMenu = action.payload;
    },
    
    setShowBottomNav: (state, action: PayloadAction<boolean>) => {
      state.ui.showBottomNav = action.payload;
    },
    
    setShowFloatingActions: (state, action: PayloadAction<boolean>) => {
      state.ui.showFloatingActions = action.payload;
    },
    
    setFullscreenWidget: (state, action: PayloadAction<string | null>) => {
      state.ui.fullscreenWidget = action.payload;
    },
    
    setSwipeInProgress: (state, action: PayloadAction<boolean>) => {
      state.ui.swipeInProgress = action.payload;
    },
    
    setPinchInProgress: (state, action: PayloadAction<boolean>) => {
      state.ui.pinchInProgress = action.payload;
    },
    
    // Лайоуты
    setCurrentLayout: (state, action: PayloadAction<string | null>) => {
      state.currentLayout = action.payload;
    },
    
    updateMobileWidgetConfig: (state, action: PayloadAction<{ layoutId: string; widgetConfig: IMobileWidgetConfig }>) => {
      const { layoutId, widgetConfig } = action.payload;
      const layout = state.mobileLayouts.find(l => l.id === layoutId);
      
      if (layout) {
        const existingIndex = layout.widgets.findIndex(w => w.id === widgetConfig.id);
        if (existingIndex >= 0) {
          layout.widgets[existingIndex] = widgetConfig;
        } else {
          layout.widgets.push(widgetConfig);
        }
        layout.updatedAt = new Date();
      }
    },
    
    removeMobileWidget: (state, action: PayloadAction<{ layoutId: string; widgetId: string }>) => {
      const { layoutId, widgetId } = action.payload;
      const layout = state.mobileLayouts.find(l => l.id === layoutId);
      
      if (layout) {
        layout.widgets = layout.widgets.filter(w => w.id !== widgetId);
        layout.updatedAt = new Date();
      }
    },
    
    // Производительность
    updatePerformanceSettings: (state, action: PayloadAction<Partial<IMobileState['performance']>>) => {
      state.performance = { ...state.performance, ...action.payload };
    },
    
    enableLowDataMode: (state) => {
      state.performance.lowDataMode = true;
      state.performance.enableLazyLoading = true;
      state.performance.enableVirtualization = true;
      state.performance.reduceAnimations = true;
    },
    
    disableLowDataMode: (state) => {
      state.performance.lowDataMode = false;
      state.performance.reduceAnimations = false;
    },
    
    // Ошибки
    clearError: (state) => {
      state.error = null;
    },
  },
  
  extraReducers: (builder) => {
    // Определение устройства
    builder.addCase(detectDevice.fulfilled, (state, action) => {
      state.deviceType = action.payload.deviceType;
      state.orientation = action.payload.orientation;
      state.viewport = action.payload.viewport;
      state.touchEnabled = action.payload.touchEnabled;
    });
    
    // Загрузка мобильных лайоутов
    builder.addCase(loadMobileLayouts.pending, (state) => {
      state.loading.layouts = true;
    });
    
    builder.addCase(loadMobileLayouts.fulfilled, (state, action) => {
      state.loading.layouts = false;
      state.mobileLayouts = action.payload;
      
      // Устанавливаем первый лайоут как текущий, если он еще не установлен
      if (!state.currentLayout && action.payload.length > 0) {
        const defaultLayout = action.payload.find(l => l.isDefault) || action.payload[0];
        if (defaultLayout) {
          state.currentLayout = defaultLayout.id;
        }
      }
    });
    
    builder.addCase(loadMobileLayouts.rejected, (state, action) => {
      state.loading.layouts = false;
      state.error = action.error.message || 'Ошибка загрузки мобильных лайоутов';
    });
    
    // Установка PWA
    builder.addCase(installPWA.pending, (state) => {
      state.loading.installation = true;
    });
    
    builder.addCase(installPWA.fulfilled, (state, action) => {
      state.loading.installation = false;
      state.pwaSettings.isInstalled = action.payload.isInstalled;
    });
    
    builder.addCase(installPWA.rejected, (state, action) => {
      state.loading.installation = false;
      state.error = action.error.message || 'Ошибка установки PWA';
    });
    
    // Push уведомления
    builder.addCase(sendPushNotification.fulfilled, (_state, action) => {
      void action;
    });
    
    builder.addCase(sendPushNotification.rejected, (state, action) => {
      state.error = action.error.message || 'Ошибка отправки уведомления';
    });
    
    // Измерение производительности
    builder.addCase(measurePerformance.fulfilled, (state, action) => {
      // Автоматически включаем режим экономии, если производительность низкая
      const metrics = action.payload;
      if (metrics.firstContentfulPaint > 3000 || metrics.totalBlockingTime > 300) {
        state.performance.lowDataMode = true;
        state.performance.reduceAnimations = true;
      }
    });
  },
});

export const {
  setDeviceType,
  setOrientation,
  updateViewport,
  updatePWASettings,
  setOnlineStatus,
  setUpdateAvailable,
  recordTouchEvent,
  setGesturesEnabled,
  setShowMobileMenu,
  setShowBottomNav,
  setShowFloatingActions,
  setFullscreenWidget,
  setSwipeInProgress,
  setPinchInProgress,
  setCurrentLayout,
  updateMobileWidgetConfig,
  removeMobileWidget,
  updatePerformanceSettings,
  enableLowDataMode,
  disableLowDataMode,
  clearError,
} = mobileSlice.actions;

export default mobileSlice.reducer; 